import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import {
  PencilSimpleLineIcon,
  GlobeSimpleIcon,
  UsersIcon
} from "@phosphor-icons/react";

import Icon from "components/Icon/Icon";
import CommonTooltip from "components/Tooltip/index";
import StatusPillButtons from "components/Buttons/StatusPillButtons";

import { CompanyNameCell, renderValue } from "utils/renderCompanyCell";

const columns = () => {
  const columnHelper = createColumnHelper();

  return [
    columnHelper.accessor("id", {
      id: "id",
      header: "ID",
      size: 96,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: true,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor((row) => row.company_name, {
      id: "company_name",
      header: "Company Name",
      size: 200,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: true,
      cell: (info) => {
        const row = info.row.original;
        return (
          <CompanyNameCell
            logo={row?.logo}
            name={renderValue(row.company_name)}
            title={renderValue(row.company_name)}
            showCompanyPlaceholder={true}
          />
        );
      }
    }),
    columnHelper.accessor("marketers_count", {
      header: "Marketers Count",
      size: 160,
      headerClassName: "table-header mono",
      enableSorting: false,
      cellClassName: "table-cell mono",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("website", {
      header: "Website",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return value && value.trim() !== "" ? (
          <div className="table-link" title={value}>
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              title={value}
            >
              {value}
            </a>
          </div>
        ) : (
          <span>-</span>
        );
      }
    }),
    columnHelper.accessor((row) => row.domains, {
      id: "domains",
      header: "Domains",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const domains = info.getValue();
        if (!domains || domains.length === 0) return "-";

        const firstDomain = domains[0]?.domain || "-";
        const remainingCount = domains.length - 1;

        // all domains for tooltip, comma-separated
        const tooltipContent = domains?.map((d) => d.domain).join(", ");

        return (
          <div className="truncate-text" title={tooltipContent}>
            {firstDomain}
            {remainingCount > 0 && ` , +${remainingCount}`}
          </div>
        );
      }
    }),
    columnHelper.accessor("status", {
      header: "Status",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return <StatusPillButtons status={value} dot={true} />;
      }
    }),
    columnHelper.accessor("has_activated_user", {
      header: "Has User Verified",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return <StatusPillButtons status={value} dot={false} />;
      }
    }),
    columnHelper.accessor("type", {
      header: "Type",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="truncate-text" title={renderValue(value)}>
            {renderValue(value)}
          </div>
        );
      }
    }),
    columnHelper.accessor((row) => row.industry?.industry_name, {
      id: "industry",
      header: "Industry Name",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="truncate-text" title={value}>
            {value || "-"}
          </div>
        );
      }
    }),
    columnHelper.accessor((row) => row.cities?.name, {
      id: "city",
      header: "City Name",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => {
        const value = info.getValue();
        return <div className="truncate-text">{value || "-"}</div>;
      }
    }),
    columnHelper.accessor("description", {
      header: "Description",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <div className="truncate-text" title={renderValue(info.getValue())}>
          {renderValue(info.getValue())}
        </div>
      )
    }),
    columnHelper.accessor("size", {
      header: "Size",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("linkedin", {
      header: "LinkedIn",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => {
        const value = info.getValue();
        return value && value.trim() !== "" ? (
          <div className="table-link">
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              title={value}
            >
              {value}
            </a>
          </div>
        ) : (
          <span>-</span>
        );
      }
    }),
    columnHelper.accessor("facebook", {
      header: "Facebook",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => {
        const value = info.getValue();
        return value && value.trim() !== "" ? (
          <div className="table-link">
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              title={value}
            >
              {value}
            </a>
          </div>
        ) : (
          <span>-</span>
        );
      }
    }),
    columnHelper.accessor("instagram", {
      header: "Instagram",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => {
        const value = info.getValue();
        return value && value.trim() !== "" ? (
          <div className="table-link">
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              title={value}
            >
              {value}
            </a>
          </div>
        ) : (
          <span>-</span>
        );
      }
    }),
    columnHelper.accessor("is_company_details_filled", {
      header: () => (
        <div className="truncate-text">Is Company Detail Filled</div>
      ),
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => {
        const value = info.getValue();
        return <span>{value}</span>;
      }
    }),
    columnHelper.accessor("created_by", {
      header: "Created By",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("country_code", {
      header: "Country Code",
      size: 120,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("phone_number", {
      header: "Phone",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("deeplink_name", {
      header: "Deeplink Name",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <div className="truncate-text" title={renderValue(info.getValue())}>
          {renderValue(info.getValue())}
        </div>
      )
    }),
    columnHelper.accessor("is_hidden", {
      header: "Is Hidden",
      size: 120,
      headerClassName: "table-header mono",
      enableSorting: false,
      cellClassName: "table-cell mono",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("source", {
      header: "Source",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <div className="truncate-text" title={renderValue(info.getValue())}>
          {renderValue(info.getValue())}
        </div>
      )
    }),
    columnHelper.accessor("created_at", {
      header: "Created At",
      size: 207,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("email", {
      header: "Email",
      size: 160,
      headerClassName: "table-header",
      enableSorting: false,
      cellClassName: "table-cell",
      cell: (info) => (
        <div className="truncate-text" title={renderValue(info.getValue())}>
          {renderValue(info.getValue())}
        </div>
      )
    }),
    columnHelper.accessor("exclude_weekly_ta_mails", {
      header: () => (
        <div className="truncate-text">Exclude Weekly TA Mails</div>
      ),
      size: 120,
      headerClassName: "table-header mono",
      enableSorting: false,
      cellClassName: "table-cell mono",
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    // columnHelper.accessor("top_talents", {
    //   header: "Top Talents",
    //   size: 160,
    //   headerClassName: "table-header",
    //   cellClassName: "table-cell",
    //   cell: (info) => {
    //     const value = info.getValue();
    //     return <span>{value}</span>;
    //   }
    // }),
    columnHelper.accessor("is_popular", {
      header: "Is Popular",
      size: 120,
      headerClassName: "table-header mono",
      cellClassName: "table-cell mono",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("roster_updated_at", {
      header: () => <div className="truncate-text">Roster Updated At</div>,
      size: 120,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => (
        <div className="truncate-text">{renderValue(info.getValue())}</div>
      )
    }),
    columnHelper.accessor("specialized_regions", {
      header: "Specialised Regions",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,

      cell: (info) => {
        const value = info.getValue();

        // Normalize all possible response formats
        const regions = Array.isArray(value)
          ? value
              .map((r) => {
                if (typeof r === "string") return r; // ["West", "South"]
                if (r?.region_name) return r.region_name; // [{ id, region_name }]
                if (r?.name) return r.name; // [{ id, name }]
                return null;
              })
              .filter(Boolean)
          : typeof value === "string"
            ? value.split(",").map((v) => v.trim())
            : [];

        // If no valid data
        if (regions.length === 0) return "-";

        // Show up to 2 items + "+N" if more
        const displayList = regions.slice(0, 2).join(", ");
        const remaining = regions.length - 2;
        const displayValue =
          remaining > 0 ? `${displayList}, +${remaining}` : displayList;

        return <div title={regions.join(", ")}>{displayValue}</div>;
      }
    }),
    columnHelper.accessor("specialized_in", {
      header: () => (
        <div className="truncate-text">Company Specialised Name</div>
      ),
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();

        // Normalize: ensure array of names
        const specializations = Array.isArray(value)
          ? value.map((r) => r?.name || "")
          : typeof value === "string"
            ? value.split(",").map((v) => v.trim())
            : [];

        if (specializations.length === 0) return "-";

        const displayList = specializations.slice(0, 2).join(", ");
        const remaining = specializations.length - 2;

        const displayValue =
          remaining > 0 ? `${displayList}, +${remaining}` : displayList;

        return <div title={specializations.join(", ")}>{displayValue}</div>;
      }
    }),
    columnHelper.accessor("services", {
      header: () => <div className="truncate-text">Services</div>,
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue(); // Expecting array of objects like [{id, name}, ...]

        // Normalize: ensure it's always an array of service names
        const services = Array.isArray(value)
          ? value.map((service) => service?.name || "")
          : typeof value === "string"
            ? value.split(",").map((v) => v.trim())
            : [];

        if (services.length === 0) return "-";

        // Take only first 2 names
        const displayList = services.slice(0, 2).join(", ");
        const remaining = services.length - 2;

        // Add “+n” if there are more
        const displayValue =
          remaining > 0 ? `${displayList}, +${remaining}` : displayList;

        return (
          <div className="truncate-text" title={services.join(", ")}>
            {displayValue}
          </div>
        );
      }
    }),
    columnHelper.accessor("tag_name", {
      header: "Tag Name",
      size: 160,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.accessor("tagged_at", {
      header: "Tagged At",
      size: 207,
      headerClassName: "table-header",
      cellClassName: "table-cell",
      enableSorting: false,
      cell: (info) => <>{renderValue(info.getValue())}</>
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      size: 140,
      cellClassName: "actions",
      enableSorting: false,
      cell: (info) => {
        const navigate = useNavigate();
        const companyId = info.row.original.id;
        const company = info.row.original;

        return (
          <div className="hover-btn-wrapper">
            <CommonTooltip theme="dark" tooltipContent="View Marketers">
              <div
                className="hover-actions"
                onClick={() =>
                  navigate(
                    `/companies/${company.id}/marketers${window.location.search}`,
                    {
                      state: { company }
                    }
                  )
                }
              >
                <Icon component={UsersIcon} format="outline" weight="regular" />
              </div>
            </CommonTooltip>

            <CommonTooltip theme="dark" tooltipContent="View Domains">
              <div
                className="hover-actions"
                onClick={() =>
                  navigate(
                    `/companies/${company.id}/domains${window.location.search}`,
                    {
                      state: { company }
                    }
                  )
                }
              >
                <Icon
                  component={GlobeSimpleIcon}
                  format="outline"
                  weight="regular"
                />
              </div>
            </CommonTooltip>

            <CommonTooltip theme="dark" tooltipContent="Edit Details">
              <div
                className="hover-actions"
                onClick={() =>
                  navigate(
                    `/companies/${company.id}${window.location.search}`,
                    { state: { company } }
                  )
                }
              >
                <Icon
                  component={PencilSimpleLineIcon}
                  format="outline"
                  weight="regular"
                />
              </div>
            </CommonTooltip>
          </div>
        );
      }
    })
  ];
};

export default columns;
